import React from 'react';

/**
 * Loading, empty and error states. Skeletons reserve the height their content
 * will occupy so nothing jumps when data lands (keeps CLS near zero).
 */

export const Spinner = ({ size = 24, className = '' }) => (
  <svg
    className={`animate-spin text-accent ${className}`}
    style={{ width: size, height: size }}
    viewBox="0 0 24 24"
    fill="none"
    role="status"
    aria-label="Loading"
  >
    <circle className="opacity-20" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
    <path className="opacity-90" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
  </svg>
);

export const Skeleton = ({ className = '' }) => (
  <div className={`animate-pulse rounded-md bg-muted ${className}`} aria-hidden="true" />
);

/** Placeholder shaped like the list it replaces, so the layout never shifts. */
export const SkeletonList = ({ count = 3, className = '' }) => (
  <div className={`space-y-4 ${className}`} aria-busy="true" aria-label="Loading content">
    {Array.from({ length: count }).map((_, i) => (
      <div key={i} className="rounded-lg border border-border bg-surface p-4 shadow-sm">
        <div className="flex gap-2">
          <Skeleton className="h-5 w-16" />
          <Skeleton className="h-5 w-24" />
        </div>
        <Skeleton className="mt-4 h-5 w-1/3" />
        <Skeleton className="mt-2 h-4 w-2/3" />
        <div className="mt-4 grid gap-2 sm:grid-cols-3">
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-full" />
        </div>
      </div>
    ))}
  </div>
);

export const PageLoader = ({ label = 'Loading…', description }) => (
  <div className="flex min-h-[50vh] flex-col items-center justify-center gap-4 px-4 text-center">
    <Spinner size={32} />
    <div>
      <p className="font-medium text-foreground">{label}</p>
      {description && <p className="mt-1 text-sm text-muted-fg">{description}</p>}
    </div>
  </div>
);

export const EmptyState = ({ icon: Icon, title, description, action, className = '' }) => (
  <div
    className={`flex flex-col items-center justify-center rounded-lg border border-dashed border-border-strong bg-surface px-6 py-12 text-center ${className}`}
  >
    {Icon && (
      <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-muted text-muted-fg">
        <Icon style={{ fontSize: 24 }} aria-hidden="true" />
      </div>
    )}
    <h3 className="text-base font-semibold text-foreground">{title}</h3>
    {description && <p className="mt-1.5 max-w-sm text-sm text-muted-fg">{description}</p>}
    {action && <div className="mt-5">{action}</div>}
  </div>
);

const ALERT_VARIANTS = {
  error: { wrap: 'bg-danger-soft border-danger/25 text-danger-fg', icon: 'text-danger' },
  warning: { wrap: 'bg-warning-soft border-warning/25 text-warning-fg', icon: 'text-warning' },
  success: { wrap: 'bg-success-soft border-success/25 text-success-fg', icon: 'text-success' },
  info: { wrap: 'bg-info-soft border-info/25 text-info-fg', icon: 'text-info' },
};

const ALERT_PATHS = {
  error: 'M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z',
  warning: 'M12 9v2m0 4h.01M5.07 19h13.86a2 2 0 001.73-3L13.73 4a2 2 0 00-3.46 0L3.34 16a2 2 0 001.73 3z',
  success: 'M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z',
  info: 'M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z',
};

export const Alert = ({ variant = 'info', title, children, action, className = '' }) => {
  const style = ALERT_VARIANTS[variant] || ALERT_VARIANTS.info;

  return (
    <div
      role={variant === 'error' ? 'alert' : 'status'}
      className={`flex items-start gap-3 rounded-lg border px-4 py-3 ${style.wrap} ${className}`}
    >
      <svg
        className={`mt-0.5 h-5 w-5 shrink-0 ${style.icon}`}
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
        aria-hidden="true"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth="2"
          d={ALERT_PATHS[variant] || ALERT_PATHS.info}
        />
      </svg>
      <div className="min-w-0 flex-1 text-sm">
        {title && <p className="font-semibold">{title}</p>}
        {children && <div className={title ? 'mt-0.5' : ''}>{children}</div>}
        {action && <div className="mt-3">{action}</div>}
      </div>
    </div>
  );
};
